import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';

const NAV = [
  { path: '/client/dashboard', icon: '📊', label: 'Dashboard' },
  { path: '/client/campaigns', icon: '📧', label: 'Campaigns' },
  { path: '/client/sequences', icon: '🔄', label: 'Sequences' },
  { path: '/client/leads', icon: '👥', label: 'Leads' },
  { path: '/client/crm', icon: '🏆', label: 'CRM' },
  { path: '/client/email-accounts', icon: '✉️', label: 'Email Accounts' },
  { path: '/client/warmup', icon: '🌡️', label: 'Warmup' },
  { path: '/client/inbox', icon: '📬', label: 'Inbox' },
  { path: '/client/verifier', icon: '✅', label: 'Email Verifier' },
  { path: '/client/ab-testing', icon: '🧪', label: 'A/B Testing' },
  { path: '/client/scheduling', icon: '📅', label: 'Scheduling' },
  { path: '/client/templates', icon: '📝', label: 'Templates' },
  { path: '/client/analytics', icon: '📈', label: 'Analytics' },
  { path: '/client/billing', icon: '💳', label: 'Billing' },
  { path: '/client/invoices', icon: '🧾', label: 'Invoices' },
  { path: '/client/support', icon: '🎫', label: 'Support' },
  { path: '/client/blacklist', icon: '🚫', label: 'Blacklist' },
  { path: '/client/unsubscribes', icon: '🔕', label: 'Unsubscribes' },
  { path: '/client/settings', icon: '⚙️', label: 'Settings' },
];

export default function ClientLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [collapsed, setCollapsed] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('theme') !== 'light');

  useEffect(() => {
    document.body.classList.toggle('light-mode', !darkMode);
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  const handleLogout = () => { logout(); navigate('/client'); };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: darkMode ? '#0A0A0A' : '#F2F4F7' }}>
      {/* Sidebar */}
      <div style={{ width: collapsed ? 60 : 238, background: darkMode ? '#080808' : '#fff', borderRight: `1px solid ${darkMode ? 'rgba(46,204,113,0.08)' : 'rgba(0,0,0,0.08)'}`, display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, left: 0, bottom: 0, transition: 'width 0.2s', zIndex: 50, overflow: 'hidden' }}>
        {/* Logo */}
        <div style={{ padding: collapsed ? '18px 14px' : '18px 16px', borderBottom: '1px solid rgba(46,204,113,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 64 }}>
          {!collapsed && (
            <Link to="/client/dashboard" style={{ textDecoration: 'none', display: 'flex', alignItems: 'baseline', gap: 4 }}>
              <span style={{ fontSize: 19, fontWeight: 900, color: '#2ECC71', letterSpacing: -0.5 }}>MARK</span>
              <span style={{ fontSize: 10, fontWeight: 500, color: '#555', marginLeft: 4 }}>Ms Discoveries</span>
            </Link>
          )}
          <button onClick={() => setCollapsed(!collapsed)} style={{ background: 'none', border: 'none', color: '#444', fontSize: 16, cursor: 'pointer', padding: 4, lineHeight: 1, flexShrink: 0 }}>
            {collapsed ? '›' : '‹'}
          </button>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '10px 6px' }}>
          {NAV.map(item => {
            const active = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path} title={collapsed ? item.label : ''} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: collapsed ? '10px 14px' : '9px 12px', borderRadius: 9, marginBottom: 1, textDecoration: 'none', background: active ? 'rgba(46,204,113,0.1)' : 'transparent', color: active ? '#2ECC71' : '#555', fontWeight: active ? 700 : 400, fontSize: 13, transition: 'all 0.15s', borderLeft: `2px solid ${active ? '#2ECC71' : 'transparent'}`, justifyContent: collapsed ? 'center' : 'flex-start' }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.color = '#ccc'; } }}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#555'; } }}>
                <span style={{ fontSize: 15, flexShrink: 0 }}>{item.icon}</span>
                {!collapsed && <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User info + logout */}
        <div style={{ padding: '10px 6px', borderTop: '1px solid rgba(46,204,113,0.06)' }}>
          {!collapsed && user && (
            <div style={{ padding: '8px 12px', marginBottom: 6, borderRadius: 9, background: 'rgba(46,204,113,0.05)', border: '1px solid rgba(46,204,113,0.08)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#ccc', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
              <div style={{ fontSize: 10, color: '#444' }}>{user?.planId?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'No Plan'}</div>
            </div>
          )}
          <button onClick={handleLogout} title={collapsed ? 'Logout' : ''} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px', background: 'none', border: 'none', color: '#444', fontSize: 13, cursor: 'pointer', borderRadius: 9, justifyContent: collapsed ? 'center' : 'flex-start', transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#E74C3C'; e.currentTarget.style.background = 'rgba(231,76,60,0.07)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#444'; e.currentTarget.style.background = 'none'; }}>
            <span>🚪</span>{!collapsed && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Main */}
      <div style={{ flex: 1, marginLeft: collapsed ? 60 : 238, transition: 'margin 0.2s', display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Topbar */}
        <div style={{ background: darkMode ? '#080808' : '#fff', borderBottom: `1px solid ${darkMode ? 'rgba(46,204,113,0.06)' : 'rgba(0,0,0,0.08)'}`, padding: '13px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 40 }}>
          <div style={{ fontSize: 13, color: '#444' }}>
            <span style={{ marginRight: 6 }}>{NAV.find(n => n.path === location.pathname)?.icon}</span>
            <span style={{ color: '#666' }}>{NAV.find(n => n.path === location.pathname)?.label || 'Dashboard'}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {user?.status === 'grace_period' && <span style={{ fontSize: 11, color: '#E74C3C', fontWeight: 700, background: 'rgba(231,76,60,0.1)', padding: '3px 10px', borderRadius: 20 }}>⚠ Grace Period</span>}
            {user?.status === 'active' && <span style={{ fontSize: 11, color: '#2ECC71', fontWeight: 700 }}>● Active</span>}
            {/* Dark/Light toggle */}
            <button onClick={() => setDarkMode(d => !d)} title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              style={{ background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.07)', border: 'none', borderRadius: 20, padding: '5px 12px', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6, color: darkMode ? '#ccc' : '#444', transition: 'all 0.2s' }}>
              {darkMode ? '☀️' : '🌙'}
              <span style={{ fontSize: 11, fontWeight: 600 }}>{darkMode ? 'Light' : 'Dark'}</span>
            </button>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg, #2ECC71, #27AE60)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 12, color: '#000' }}>
              {user?.name?.[0]?.toUpperCase()}
            </div>
          </div>
        </div>

        {/* Impersonation Banner */}
        {localStorage.getItem('isImpersonating') === 'true' && (
          <div style={{ background:'rgba(231,76,60,0.15)', borderBottom:'2px solid #E74C3C', padding:'10px 28px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ color:'#E74C3C', fontWeight:700, fontSize:13 }}>🛡 Admin View — You are viewing as this client</span>
            <button onClick={() => {
              const adminToken = localStorage.getItem('adminToken');
              if (adminToken) {
                localStorage.setItem('token', adminToken);
                localStorage.removeItem('adminToken');
                localStorage.removeItem('isImpersonating');
                window.location.href = '/admin/clients';
              }
            }} style={{ padding:'6px 16px', background:'#E74C3C', border:'none', borderRadius:8, color:'#fff', fontWeight:700, cursor:'pointer', fontSize:12 }}>
              ← Return to Admin
            </button>
          </div>
        )}
        {/* Page Content */}
        <div style={{ flex: 1, padding: '28px 32px', maxWidth: 1400, background: darkMode ? '#0A0A0A' : '#F2F4F7' }}>
          {children}
        </div>
      </div>
    </div>
  );
}
